/**
 * Neutralise dollar amounts so they are not read as LaTeX math delimiters.
 *
 * `remarkMath` treats `$...$` as inline math, and a reviewer writing about cost
 * hands it several delimiters in one paragraph. A real comment read:
 *
 *   low-cost (<$500), but ... a 3D printer (~$2,000) and a laser cutter
 *   (~$10,000), representing ... approximately $12,000.
 *
 * Four dollars became two math spans, and the PROSE between them was typeset
 * as maths — letters italicised as variables, spaces stripped, words stacked
 * vertically. Worse, the resulting element is unbreakable and 790px wide
 * inside a 352px column, so it set the scroll width for the entire comment
 * list: one comment made every comment scroll sideways.
 *
 * We match a currency-shaped run (digits, comma thousands, decimal cents) and
 * only escape when what follows can't be math continuation. Real math closes on
 * the next `$` (`$2$`), or continues into a LaTeX command (`$2\pi$`), a variable
 * (`$3n+1$`), a group (`$2^{-\phi}$`), or an operator (`$0 < \epsilon < 1$`).
 *
 * An operator alone doesn't settle it — `$1/2$` is maths but `$5/month` is a
 * price — so we look past the operator: a digit, command, or single-letter
 * variable means maths, a whole word means prose. Everything else — sentence
 * punctuation, brackets, end-of-input, or a following `$` — is currency.
 *
 * That last case matters: an amount followed by whitespace and another `$`
 * ("costs are $3, $5, and $10") must be escaped. Leaving it lets remark-math
 * pair the surviving `$` with the next escaped one and typeset the prose
 * between them — the original bug, in a different syntactic shape.
 */
export function escapeCurrency(markdown: string): string {
  return markdown.replace(/(?<!\\)\$\d[\d,]*(?:\.\d+)?/g, (match, offset, source) => {
    const rest: string = source.slice(offset + match.length)
    const immediate = rest[0] ?? ''

    // Closing delimiter, LaTeX command, variable, or group — all maths.
    if (/^~+\s*\\[A-Za-z]+/.test(rest)) return match // $10~\mu\mathrm{g/ml}$
    if (immediate === '$' || /[\w\\^{]/.test(immediate)) return match

    // An operator is only maths if a maths token follows it.
    const operator = rest.match(/^[ \t]*[<>=+\-*/^_][ \t]*(\S?)([A-Za-z]*)/)
    if (operator) {
      const head = operator[1] ?? ''
      if (/[\d\\({.]/.test(head)) return match // $1/2$, $2 \times 10^{-3}$
      if (/^[A-Za-z]$/.test(head + (operator[2] ?? ''))) return match // $1 < p < \infty$
      return '\\' + match // $5/month, $5-$10
    }

    // Past a space, only a LaTeX command reads as maths.
    if (rest.match(/^[ \t]+(\S)/)?.[1] === '\\') return match // $1 \leq k \leq n$
    return '\\' + match
  })
}
